import { useState } from 'react';
import type { ChatSummary, MessageDto, TranscriptView } from '../api/client.ts';
import type { MessageView } from '../chat/ChatContext.tsx';
import { MAX_ALBUM_ITEMS } from '../lib/album.ts';
import { findMatches } from '../lib/search.ts';
import { AlbumGrid } from '../components/AlbumBubble.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { UpdateBanner } from '../components/Banners.tsx';
import { CameraScreen } from '../components/CameraScreen.tsx';
import { Composer } from '../components/Composer.tsx';
import { ImageViewer } from '../components/ImageViewer.tsx';
import { VoiceBubble } from '../components/MediaBubbles.tsx';
import {
  ChatMatches,
  SearchField,
  SearchNavBar,
  SearchResults,
} from '../components/MessageSearch.tsx';
import '../components/ChatList.css';
import '../components/MessageThread.css';
import './NewGroupScreen.css';
import './ProfileScreen.css';

/**
 * A bench for components whose behaviour is hard to reach from the app.
 *
 * The viewer is the reason it exists: checking a pinch or a double tap used to
 * mean logging in as somebody real, finding a conversation with a photo in it
 * and opening that — three steps that touch live data before the first gesture.
 * Here it mounts on its own, over a picture the dev server invents (see the
 * `dev-sample-photo` plugin in vite.config.ts), with no session involved.
 *
 * Mounted only under `import.meta.env.DEV`; nothing here reaches a build.
 */

/**
 * The sizes an avatar is drawn at around the app. The classes are the real
 * ones, so what is on screen here is what the chat list and the call screen
 * will do with the same file rather than an approximation of it. They are all
 * the same rounded square; the row is here to check that the corner still
 * looks like the same corner once the tile is four times as big.
 */
const AVATAR_SHAPES = [
  { label: 'список 42px', className: 'chat-row-avatar' },
  { label: 'ростер 40px', className: 'group-pick-avatar' },
  { label: 'профиль 96px', className: 'profile-avatar' },
] as const;

/** Enough of a message for a bubble to draw itself; the rest is never read. */
function voiceMessage(id: string, transcript: TranscriptView | null): MessageView {
  return {
    id,
    conversationId: 'dev',
    fromMe: false,
    senderId: 'dev',
    senderName: 'dev',
    senderAvatarId: null,
    kind: 'voice',
    body: null,
    fileName: 'voice.webm',
    fileSize: 40_000,
    mime: 'audio/webm',
    createdAt: Date.now(),
    readAt: null,
    editedAt: null,
    durationMs: 41_000,
    trimStartMs: null,
    trimEndMs: null,
    viewedAt: null,
    aiNotice: null,
    aiImage: null,
    callResult: null,
    callVideo: null,
    reactions: [],
    replyTo: null,
    forwardedFrom: null,
    transcript,
    attachments: [],
    status: 'sent',
  };
}

/**
 * The five states a voice bubble can be in, which are otherwise reachable only
 * with a speech engine configured and a recording that actually fails in the
 * right way. `<audio>` will 404 here — there is no session and no file — and
 * that is fine: nothing on this page is about playback.
 */
const TRANSCRIPT_STATES: { label: string; transcript: TranscriptView | null }[] = [
  { label: 'никто не просил', transcript: null },
  { label: 'идёт', transcript: { status: 'pending', text: null, at: null } },
  {
    label: 'готово',
    transcript: {
      status: 'done',
      text: 'Слушай, я освобожусь около семи. Давай встретимся у входа в парк, там где мы в прошлый раз стояли.',
      at: Date.now(),
    },
  },
  { label: 'тишина', transcript: { status: 'done', text: null, at: Date.now() } },
  { label: 'ошибка', transcript: { status: 'failed', text: null, at: Date.now() } },
];

/** Every shape the update banner has, including the two nobody wants to reach. */
const UPDATE_STATES: {
  stage: 'offer' | 'downloading' | 'ready' | 'failed';
  progress: number | null;
  message: string | null;
}[] = [
  { stage: 'offer', progress: null, message: null },
  { stage: 'downloading', progress: 0.42, message: null },
  { stage: 'downloading', progress: null, message: null },
  {
    stage: 'ready',
    progress: null,
    message: 'Разрешите установку из этого источника и нажмите «Установить» ещё раз.',
  },
  { stage: 'failed', progress: null, message: 'Файл повреждён при загрузке' },
];

/**
 * A conversation to search through, the same words the canvas is drawn with, so
 * the empty state and the count can be reached by typing rather than by having
 * a real chat with the right history in it.
 */
const SEARCH_SAMPLE: MessageDto[] = [
  ['Игорь', 'Отправил договор на подпись, посмотри второй пункт'],
  ['Вы', 'Ок, вечером гляну'],
  ['Игорь', 'Счёт по договору выставлю после подписания'],
  ['Вы', 'Приложил старый договор для сравнения'],
  ['Игорь', 'Юристы вернули договор с правками, файл во вложении'],
  ['Вы', 'Счёт оплатили, пришлите закрывающие документы'],
].map(([who, body], index) => ({
  id: `dev-search-${index}`,
  conversationId: index % 3 === 0 ? 'dev-group' : 'dev-direct',
  fromMe: who === 'Вы',
  senderId: 'dev',
  senderName: who!,
  senderAvatarId: null,
  kind: 'text',
  body: body!,
  fileName: null,
  fileSize: null,
  mime: null,
  createdAt: Date.now() - index * 86_400_000,
  readAt: null,
  editedAt: null,
  durationMs: null,
  trimStartMs: null,
  trimEndMs: null,
  viewedAt: null,
  aiNotice: null,
  aiImage: null,
  callResult: null,
  callVideo: null,
  reactions: [],
  replyTo: null,
  forwardedFrom: null,
  transcript: null,
  attachments: [],
}));

/** The two conversations the sample messages above are spread across. */
const SEARCH_CHATS = [
  { id: 'dev-direct', kind: 'direct', title: 'Игорь Петров', peer: null, avatarPhotoId: null },
  { id: 'dev-group', kind: 'group', title: 'Работа', peer: null, avatarPhotoId: null },
] as unknown as ChatSummary[];

export function DevPreviewScreen() {
  const [viewer, setViewer] = useState(false);
  const [camera, setCamera] = useState(false);
  /** The search field, driven for real: type here and the list below follows. */
  const [query, setQuery] = useState('договор');
  /** The last thing filmed or shot here, so the screen can be judged by it. */
  const [shot, setShot] = useState<{ name: string; size: number; url: string } | null>(null);
  /** What the composer below would have sent, since there is no conversation here. */
  const [pasted, setPasted] = useState<string | null>(null);

  return (
    <div style={{ padding: 24, fontFamily: 'system-ui', display: 'grid', gap: 12, justifyItems: 'start' }}>
      <h1 style={{ margin: 0, fontSize: 20 }}>Dev preview</h1>
      <p style={{ margin: 0, opacity: 0.7 }}>
        Not part of the app. Add a component here when driving it by hand is the only way to see it.
      </p>

      <button type="button" onClick={() => setViewer(true)}>
        ImageViewer — pinch, drag, double tap
      </button>

      {viewer && (
        <ImageViewer
          image={{ id: 'dev-sample', name: 'dev-sample.svg' }}
          onClose={() => setViewer(false)}
        />
      )}

      {/* Reaching this in the app is a login, a conversation, the paperclip and
          then the menu, and none of the four says anything about the camera. The
          modes, the flash and the recording clock are all judged by eye, so this
          is the only sensible way to look at them. What comes back is reported
          rather than sent — there is no conversation behind this button. */}
      <button type="button" onClick={() => setCamera(true)}>
        CameraScreen — Фото/Видео/Кружочек, вспышка, флип
      </button>
      {shot && (
        <p style={{ margin: 0, fontSize: 12, opacity: 0.7 }}>
          {shot.name} — {Math.round(shot.size / 1024)} КБ ·{' '}
          <a href={shot.url} target="_blank" rel="noreferrer">
            открыть
          </a>
        </p>
      )}

      {camera && (
        <CameraScreen
          // A round number rather than the server's: nothing here uploads, and
          // this is the knob that decides how long a clip may run.
          maxUploadBytes={50 * 1024 * 1024}
          onCapture={(file) => {
            setCamera(false);
            setShot({ name: file.name, size: file.size, url: URL.createObjectURL(file) });
          }}
          // No trim screen here — the take is reported the same way a clip is,
          // which is enough to judge the mode by.
          onNote={(recorded) => {
            setCamera(false);
            setShot({
              name: `кружочек, ${Math.round(recorded.durationMs / 1000)} с`,
              size: recorded.blob.size,
              url: URL.createObjectURL(recorded.blob),
            });
          }}
          onGallery={() => setCamera(false)}
          onSystemCamera={null}
          onClose={() => setCamera(false)}
        />
      )}

      {/* Pasting a picture into the composer, which is otherwise a login, a
          conversation and a clipboard away. What the paste produces — the
          compose screen, the caption, the grouping — is the album's own, so the
          whole of the feature is visible here. Nothing is sent: the bar reports
          what it would have sent instead. */}
      <h2 style={{ margin: '16px 0 0', fontSize: 16 }}>Composer — вставка картинки из буфера</h2>
      {pasted && (
        <p style={{ margin: 0, fontSize: 12, opacity: 0.7 }}>{pasted}</p>
      )}
      <div style={{ width: 420, maxWidth: '100%', background: 'var(--thread-bg, #eef1f2)', padding: 8 }}>
        <Composer
          onSendText={(body) => setPasted(`текст: ${body}`)}
          onSendFile={(file) => setPasted(`файл: ${file.name}`)}
          onSendMedia={() => setPasted('запись')}
          onSendAlbum={(album) =>
            setPasted(
              `альбом: ${album.items.map((item) => item.file.name).join(', ')}` +
                (album.caption ? ` · подпись «${album.caption}»` : ''),
            )
          }
          maxUploadBytes={50 * 1024 * 1024}
          onTyping={() => {}}
          editing={null}
          replying={null}
          onSaveEdit={() => {}}
          onCancelEdit={() => {}}
          onCancelReply={() => {}}
        />
      </div>

      {/* Every count from one to ten, which is the one thing about an album that
          is hard to see in the app: reaching the ten-tile layout means picking
          ten photographs and sending them, once per shape. The sample picture is
          invented by the dev server (see vite.config.ts) and stands in for all
          of them. */}
      <h2 style={{ margin: '16px 0 0', fontSize: 16 }}>AlbumGrid — раскладки 1–10</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, background: '#fafbfb', padding: 12 }}>
        {Array.from({ length: MAX_ALBUM_ITEMS }, (_, index) => index + 1).map((count) => (
          <div key={count} style={{ display: 'grid', gap: 4, justifyItems: 'start' }}>
            <span style={{ fontSize: 12, opacity: 0.6 }}>{count}</span>
            <AlbumGrid
              items={Array.from({ length: count }, (_, at) => ({
                src: '/api/files/dev-sample',
                // Every third one a video, so the play badge and the duration
                // pill are on screen in every layout that can hold one.
                kind: at % 3 === 2 ? 'video' : 'photo',
                durationMs: at % 3 === 2 ? 14_000 + at * 1000 : null,
              }))}
            />
          </div>
        ))}
      </div>

      {/* The one thing about an avatar that is only judgeable by eye: whether
          the same uncropped picture reads correctly at each size it is put in.
          Both rows draw the same file — the second with no photo at all, which
          is what an account that has not set one gets. */}
      <h2 style={{ margin: '16px 0 0', fontSize: 16 }}>Avatar — размеры и запасной вариант</h2>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, background: '#fafbfb', padding: 12 }}>
        {AVATAR_SHAPES.map(({ label, className }) => (
          <div key={label} style={{ display: 'grid', gap: 4, justifyItems: 'center' }}>
            <Avatar className={className} photoId="dev-sample" name="Аня" />
            <Avatar className={className} photoId={null} name="Аня" />
            <span style={{ fontSize: 12, opacity: 0.6 }}>{label}</span>
          </div>
        ))}
      </div>

      {/* The fourth one is in its gutter rather than in the row above: it is
          positioned out of the flow by 40px, so on its own it would land
          somewhere meaningless. Here the margin that opens the gutter is doing
          its half of the job too. */}
      {/* The thread paints no background of its own any more (the wallpaper
          lives on .conversation-body in the real screen), so the preview
          restores the plain one to stay comparable. */}
      <div
        className="thread thread-group"
        style={{ maxWidth: 420, minHeight: 0, flex: 'none', background: 'var(--thread-bg)' }}
      >
        <div className="message message-in">
          <Avatar className="message-avatar" photoId="dev-sample" name="Аня" />
          <div className="message-sender">Аня Смирнова</div>
          <div className="bubble bubble-in bubble-text">Первое сообщение серии — с лицом</div>
        </div>
        <div className="message message-in">
          <div className="bubble bubble-in bubble-text">Второе — без, жёлоб держит колонку</div>
        </div>
      </div>

      {/* The extreme case of what this page is for: the update banner cannot
          appear in a browser at all — `hasNativeUpdate` is false off the Android
          shell — so without this there is nowhere to look at it that is not a
          phone with an out-of-date APK on it. All four states at once, since
          three of them need a download to have gone a particular way. */}
      <h2 style={{ margin: '16px 0 0', fontSize: 16 }}>UpdateBanner — состояния</h2>
      <div style={{ display: 'grid', gap: 10, alignSelf: 'stretch' }}>
        {UPDATE_STATES.map((state) => (
          <UpdateBanner
            key={state.stage + String(state.message)}
            versionName="1.1"
            stage={state.stage}
            progress={state.progress}
            message={state.message}
            onAct={() => {}}
            onDismiss={() => {}}
          />
        ))}
      </div>

      <h2 style={{ margin: '16px 0 0', fontSize: 16 }}>Поиск по сообщениям</h2>
      <p style={{ margin: 0, opacity: 0.7 }}>
        Наберите «договор», «счёт» или «во» — список ниже отвечает на каждое слово.
      </p>
      <div style={{ width: 390, display: 'grid', gap: 10, border: '1px solid #ddd', borderRadius: 12 }}>
        <SearchField query={query} onQuery={setQuery} onBack={() => setQuery('')} />
        <div style={{ height: 320, display: 'flex', flexDirection: 'column' }}>
          <SearchResults
            query={query}
            results={SEARCH_SAMPLE.filter((message) =>
              findMatches(message.body ?? '', query).length > 0,
            )}
            total={
              SEARCH_SAMPLE.filter((message) => findMatches(message.body ?? '', query).length > 0)
                .length
            }
            loading={false}
            hasMore={false}
            onLoadMore={() => {}}
            onOpen={() => {}}
          />
        </div>
        <SearchNavBar
          position={2}
          total={4}
          createdAt={SEARCH_SAMPLE[0]!.createdAt}
          onOlder={() => {}}
          onNewer={() => {}}
        />
      </div>

      <h2 style={{ margin: '16px 0 0', fontSize: 16 }}>Поиск с главной — строки под списком чатов</h2>
      <div className="chat-list" style={{ width: 390, border: '1px solid #ddd', borderRadius: 12 }}>
        <ChatMatches
          query={query}
          results={SEARCH_SAMPLE.filter(
            (message) => findMatches(message.body ?? '', query).length > 0,
          )}
          total={
            SEARCH_SAMPLE.filter((message) => findMatches(message.body ?? '', query).length > 0)
              .length
          }
          hasMore
          onLoadMore={() => {}}
          chats={SEARCH_CHATS}
          onOpen={() => {}}
        />
      </div>

      <h2 style={{ margin: '16px 0 0', fontSize: 16 }}>VoiceBubble — расшифровка</h2>
      <div style={{ display: 'grid', gap: 14, justifyItems: 'start' }}>
        {TRANSCRIPT_STATES.map(({ label, transcript }) => (
          <div key={label} style={{ display: 'grid', gap: 4, justifyItems: 'start' }}>
            <span style={{ fontSize: 12, opacity: 0.6 }}>{label}</span>
            <div className="message message-in">
              <div className="bubble bubble-in bubble-voice">
                <VoiceBubble
                  message={voiceMessage(`dev-${label}`, transcript)}
                  playing={false}
                  onToggle={() => {}}
                  onEnded={() => {}}
                  canTranscribe
                  // Nothing to ask: there is no conversation behind this bubble.
                  onTranscribe={() => {}}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
